"use client";

import React from "react";
import { MapPin, ArrowRight, CircleCheck as CheckCircle2, UserCheck, ChartLine as LineChart, ShieldCheck, Building2, PhoneCall } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  icon: MapPin,
  title: "Milton Keynes & Local Focus",
  description: "Hyper-targeted local SEO strategies designed specifically for businesses in Milton Keynes, Buckinghamshire, and surrounding regions."
}, {
  icon: UserCheck,
  title: "100% Direct Consultant Access",
  description: "You work directly with me, Yassine Essaadi. No account managers, junior staff, or automated agency templates."
}, {
  icon: LineChart,
  title: "Measurable Local ROI",
  description: "Transparent rank tracking, Google Business Profile insights, and clear reporting focused on real lead generation."
}];
const featureBullets = ["Direct access to Yassine Essaadi — Senior SEO Specialist", "Google Business Profile & Map Pack optimization", "Hyper-local content & geo-targeted landing pages", "Clean technical SEO, local schema & citation building"];
export default function Localhero() {
  return <section id="localhero" data-nav="light" className="relative w-full bg-background text-foreground py-12 md:py-20 lg:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-6 sm:p-10 lg:p-16 shadow-2xl border border-primary/20">
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 flex flex-col items-start space-y-6">
              
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-accent text-accent-foreground hover:bg-accent/90 border-0 px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-medium shadow-xs">
                  <MapPin className="size-3.5 mr-1 shrink-0" />
                  Milton Keynes & Buckinghamshire
                </Badge>
                <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10 px-3 py-1 rounded-full text-xs font-normal">
                  <ShieldCheck className="size-3.5 mr-1 shrink-0" />
                  Independent Freelance SEO
                </Badge>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight">
                Local SEO Consultant in Milton Keynes
              </h2>

              <p className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl leading-relaxed">
                I am Yassine Essaadi, an independent SEO strategy consultant. I help Milton Keynes and UK businesses dominate local search results, rank higher on Google Maps, and turn organic traffic into paying clients. When you partner with YE Consultancy, you work directly with me—never an agency middleman.
              </p>

              <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 pb-2">
                {featureBullets.map((bullet, i) => <div key={i} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-primary-foreground/95 font-medium">
                      {bullet}
                    </span>
                  </div>)}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg font-semibold rounded-xl text-base h-13 px-7 cursor-pointer">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="ml-2 size-5" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 rounded-xl text-base h-13 px-6 cursor-pointer">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                  </Link>
                </Button>
              </div>

              <div className="flex items-center gap-2 pt-1 text-xs text-primary-foreground/80">
                <PhoneCall className="size-3.5 text-accent" />
                <span>Need immediate guidance? Call direct: <a href="tel:+447490984430" className="underline hover:text-primary-foreground font-medium">+44 7490984430</a></span>
              </div>

            </div>

            <div className="lg:col-span-5 relative mt-4 lg:mt-0">
              <div className="relative rounded-2xl bg-card text-card-foreground p-3 sm:p-4 shadow-2xl border border-border/20">
                
                <div className="absolute -top-3 -right-3 z-20 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                  <Building2 className="size-3.5" />
                  Local Expert
                </div>

                <div className="relative rounded-xl overflow-hidden aspect-4/5 bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant in Milton Keynes" className="w-full h-full object-cover object-top" loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="font-serif font-bold text-lg text-white">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-white/90">
                      Founder & Senior SEO Consultant
                    </p>
                  </div>
                </div>

                <div className="mt-3 p-3 bg-muted/60 rounded-xl border border-border/50 flex items-center justify-between text-xs sm:text-sm">
                  <span className="font-medium text-foreground">
                    Direct Client Consultation
                  </span>
                  <span className="bg-primary/10 text-primary font-semibold px-2.5 py-0.5 rounded-md text-xs">
                    100% Hands-On
                  </span>
                </div>

              </div>
            </div>

          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 sm:mt-16">
          {trustHighlights.map((item, i) => {
          const Icon = item.icon;
          return <div key={i} data-index={i} className="bg-card text-card-foreground p-6 sm:p-8 rounded-2xl border border-border shadow-md hover:shadow-lg transition-all flex flex-col justify-between">
                <div>
                  <div className="size-12 rounded-xl bg-secondary text-secondary-foreground flex items-center justify-center mb-5">
                    <Icon className="size-6 text-primary" />
                  </div>
                  <h3 className="font-serif font-semibold text-lg sm:text-xl text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="font-sans text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

      </div>
    </section>;
}